import React, { useEffect, useState } from 'react';
import { admin, files as filesApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, BadgeCheck, X, RefreshCw, User, ShieldQuestion } from 'lucide-react';

export default function AdminDatingVerificationsPanel() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setRows(null);
    try { setRows(await admin.listDatingVerifications(status)); }
    catch { toast({ title: 'Failed to load verifications' }); setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const decide = async (row, action) => {
    let reason = null;
    if (action === 'reject') {
      reason = window.prompt('Reason (shown to the user):', 'The selfie was unclear or didn\'t match your profile photos. Try again with better lighting.');
      if (reason === null) return;
    } else {
      if (!window.confirm(`Approve verification for ${row.name}?`)) return;
    }
    setBusyId(row.userId);
    try {
      await admin.decideDatingVerification(row.userId, action, reason);
      toast({ title: action === 'approve' ? 'Verified' : 'Rejected' });
      await load();
    } catch { toast({ title: 'Action failed' }); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4" data-testid="admin-verifications-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><BadgeCheck size={18}/> Selfie verifications</h2>
          <p className="text-xs text-gray-400">Approve a selfie if it matches the profile photos and looks like a real, clear self-portrait.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={status} onChange={e => setStatus(e.target.value)}
            data-testid="admin-verifications-status-filter"
            className="bg-black text-white border border-[#1f1f1f] rounded-lg px-3 py-1.5 text-sm">
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <button onClick={load} className="text-xs px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-green-500 text-gray-300 inline-flex items-center gap-1">
            <RefreshCw size={12}/> Refresh
          </button>
        </div>
      </div>

      {rows === null ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="pbb-card p-10 text-center" data-testid="admin-verifications-empty">
          <ShieldQuestion className="mx-auto text-gray-500" size={26}/>
          <div className="mt-3 font-semibold text-white">No {status} verifications</div>
          <p className="text-sm text-gray-400 mt-1">Nothing to review right now.</p>
        </div>
      ) : (
        <ul className="space-y-4" data-testid="admin-verifications-list">
          {rows.map(r => (
            <li key={r.userId} className="pbb-card p-4" data-testid={`admin-verify-${r.userId}`}>
              <div className="flex items-start gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Selfie</div>
                  {r.verificationSelfie
                    ? <img src={filesApi.urlFor(r.verificationSelfie)} alt="selfie" className="w-40 h-40 object-cover rounded-lg border border-[#2a2a2a]"/>
                    : <div className="w-40 h-40 rounded-lg border border-[#2a2a2a] flex items-center justify-center text-gray-500"><User size={40}/></div>}
                </div>
                <div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Profile photos</div>
                  <div className="grid grid-cols-3 gap-1 max-w-[240px]">
                    {(r.profilePhotos || []).slice(0, 5).map((u, i) => (
                      <img key={i} src={filesApi.urlFor(u)} alt="profile" className="w-20 h-20 object-cover rounded border border-[#2a2a2a]"/>
                    ))}
                    {(!r.profilePhotos || r.profilePhotos.length === 0) && <div className="text-xs text-gray-500">No profile photos.</div>}
                  </div>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-semibold text-white flex items-center gap-2 flex-wrap">
                    {r.name}
                    {r.verified && <BadgeCheck size={14} className="text-green-400" />}
                  </div>
                  <div className="text-xs text-gray-400 truncate">{r.email}</div>
                  <div className="text-xs text-gray-500 mt-1">Submitted {r.verificationSubmittedAt ? new Date(r.verificationSubmittedAt).toLocaleString() : '—'}</div>
                  {r.verificationRejectedReason && (
                    <div className="mt-2 text-xs bg-red-500/10 border border-red-500/30 text-red-300 rounded p-2">
                      Previous reject reason: {r.verificationRejectedReason}
                    </div>
                  )}
                </div>
                {status === 'pending' && (
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <button onClick={() => decide(r, 'approve')} disabled={busyId === r.userId}
                      data-testid={`admin-verify-approve-${r.userId}`}
                      className="text-xs px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-white font-semibold inline-flex items-center gap-1 disabled:opacity-60">
                      <BadgeCheck size={12}/> Approve
                    </button>
                    <button onClick={() => decide(r, 'reject')} disabled={busyId === r.userId}
                      data-testid={`admin-verify-reject-${r.userId}`}
                      className="text-xs px-3 py-1.5 rounded-lg border border-red-500/50 text-red-300 hover:bg-red-500/10 inline-flex items-center gap-1 disabled:opacity-60">
                      <X size={12}/> Reject
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
